import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../hooks/useAuth';
import { getBadgeByCode, redeemCode, BadgeByCodeInfo } from '../api/badge-access';

type PageState = 'loading' | 'ready' | 'not-found' | 'claiming' | 'claimed' | 'error';

export default function ClaimQRPage() {
    const { code } = useParams<{ code: string }>();
    const { user, loading: authLoading } = useAuth();

    const [pageState, setPageState] = useState<PageState>('loading');
    const [badge, setBadge] = useState<BadgeByCodeInfo | null>(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!code) {
            setPageState('not-found');
            return;
        }
        getBadgeByCode(code)
            .then((res) => {
                if (res.success && res.data) {
                    setBadge(res.data);
                    setPageState('ready');
                } else {
                    setPageState('not-found');
                }
            })
            .catch(() => setPageState('not-found'));
    }, [code]);

    const handleClaim = async () => {
        if (!code) return;
        setPageState('claiming');
        try {
            const res = await redeemCode(code);
            if (res.success) {
                setPageState('claimed');
            } else {
                setErrorMessage(res.message ?? 'Unable to claim this badge.');
                setPageState('error');
            }
        } catch {
            setErrorMessage('Something went wrong. Please try again.');
            setPageState('error');
        }
    };

    return (
        <Box
            minHeight="100vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="background.default"
            sx={{ p: 2 }}
        >
            <Card sx={{ width: '100%', maxWidth: 460 }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'accent.main', mb: 1 }} />

                    {/* Loading */}
                    {(pageState === 'loading' || authLoading) && (
                        <>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography variant="h6" fontWeight={700}>
                                Looking up your badge…
                            </Typography>
                        </>
                    )}

                    {/* Badge found — ready to claim */}
                    {pageState === 'ready' && !authLoading && badge && (
                        <>
                            {badge.badgeImage ? (
                                <Box
                                    component="img"
                                    src={badge.badgeImage}
                                    alt={badge.badgeName}
                                    sx={{ width: 120, height: 120, objectFit: 'contain', mb: 2 }}
                                />
                            ) : (
                                <WorkspacePremiumIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            )}

                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {badge.badgeName}
                            </Typography>

                            {badge.issuerName && (
                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.75} mb={1}>
                                    <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {badge.issuerName}
                                    </Typography>
                                </Box>
                            )}

                            {badge.description && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                        {badge.description}
                                    </Typography>
                                </>
                            )}

                            <Divider sx={{ my: 3 }} />

                            {user ? (
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    onClick={handleClaim}
                                >
                                    Claim this Badge
                                </Button>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        Sign in to claim this badge.
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/login"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                    >
                                        Sign In to Claim
                                    </Button>
                                    <Button
                                        component={Link}
                                        to="/register"
                                        color="secondary"
                                        size="large"
                                        fullWidth
                                        sx={{ mt: 1 }}
                                    >
                                        Create an Account
                                    </Button>
                                </>
                            )}
                        </>
                    )}

                    {/* Claiming in progress */}
                    {pageState === 'claiming' && (
                        <>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography variant="h6" fontWeight={700}>
                                Claiming your badge…
                            </Typography>
                        </>
                    )}

                    {/* Success */}
                    {pageState === 'claimed' && (
                        <>
                            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
                            <Typography variant="h5" fontWeight={700} mb={1}>
                                Badge Claimed!
                            </Typography>
                            {badge && (
                                <Typography variant="body1" color="text.secondary" mb={3}>
                                    <strong>{badge.badgeName}</strong> has been added to your collection.
                                </Typography>
                            )}
                            <Button
                                component={Link}
                                to="/available-badges"
                                variant="contained"
                                fullWidth
                                sx={{ mb: 1 }}
                            >
                                Browse Badges
                            </Button>
                            <Button
                                component={Link}
                                to="/portfolio"
                                color="secondary"
                                fullWidth
                            >
                                View My Portfolio
                            </Button>
                        </>
                    )}

                    {/* Not found */}
                    {pageState === 'not-found' && (
                        <>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 1 }} />
                            <Typography variant="h5" fontWeight={700} mb={1}>
                                Code Not Found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                This code is not valid or has already been claimed.
                            </Typography>
                            <Button component={Link} to="/available-badges" fullWidth>
                                Browse Available Badges
                            </Button>
                        </>
                    )}

                    {/* Error */}
                    {pageState === 'error' && (
                        <>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 1 }} />
                            <Typography variant="h5" fontWeight={700} mb={1}>
                                Claim Failed
                            </Typography>
                            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                                {errorMessage}
                            </Alert>
                            <Button
                                fullWidth
                                onClick={() => setPageState('ready')}
                            >
                                Try Again
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
